<div class="connect-event-section" {multiplePlans} {availableEvents} {allEvents} {planIds}>
    {#if !multiplePlans}
        <h2 class="connect-event-section__heading">{i18n.connectToEvent_Heading}</h2>
        <div class="connect-event-section__description">{i18n.connectToEvent_Description}</div>
    {/if}
    <List>
        <thead slot="header">
            <ListElement header>
                <CheckboxCell
                    bind:checked={headerChecked}
                    text={i18n.selectAll}
                    header
                    disabled={multiplePlans}
                    on:change={selectAll}
                />
                <TextCell text={i18n.time} header />
                <TextCell text={i18n.eventTitle} header />
                <TextCell text={i18n.participants} header />
            </ListElement>
        </thead>

        <tbody slot="body">
            {#if availableEvents.length > 0}
                {#each availableEvents as availableEvent}
                    <ListElement bind:checked={availableEvent.eventConnected}>
                        <CheckboxCell
                            text="Checked"
                            bind:checked={availableEvent.eventConnected}
                            disabled={availableEvent.eventOccupied}
                            on:change={selectOne(availableEvent)}
                        />
                        <TextCell text={availableEvent.eventDate} />
                        <TextCell text={availableEvent.eventTitle} />
                        <TextCell
                            text={availableEvent.participants.length > 0
                                ? availableEvent.participants
                                : i18n.allParticipants}
                        />
                    </ListElement>
                {/each}
            {:else}
                <ListElement>
                    <td colspan="4" class="connect-event-section__emptystate">
                        <span class="prom-cell__data prom-text-cell--plain"
                            >{i18n.noExistingEventsConnected}</span
                        >
                    </td>
                </ListElement>
            {/if}
        </tbody>
    </List>
</div>

<script>
    import List from "@itslearning/prometheus/assets/lists/List/v1/List.svelte";
    import CheckboxCell from "@itslearning/prometheus/assets/lists/CheckboxCell/v1/CheckboxCell.svelte";
    import ListElement from "@itslearning/prometheus/assets/lists/ListElement/v1/ListElement.svelte";
    import TextCell from "@itslearning/prometheus/assets/lists/TextCell/v1/TextCell.svelte";

    export let i18n = {};
    export let multiplePlans;
    export let availableEvents;
    export let allEvents;
    export let planIds;

    let availableEventsOriginally = [];
    let headerChecked = false;

    // Whenever there is a change in available events, we need to check if any of them should be disabled
    $: if (!multiplePlans && availableEvents && availableEvents.length > 0) {
        let connectingEvents = [];

        connectingEvents = availableEvents.filter(p => p.eventConnected);

        if (connectingEvents.length > 0) {
            // Use logic to fill out based on the first checked event, could be "all participants" or "group" event
            selectOne(connectingEvents[0]);
        } else {
            // None checked so update based on first available event that is not occupied
            const eventsFree = availableEvents.filter(p => !p.eventOccupied);

            if (eventsFree.length > 0) {
                selectOne(eventsFree[0]);
            }
        }
    }

    // Function to check whether the "check all" checkbox should be checked or not
    function updateHeaderChecked() {
        let takenEvents = [];

        takenEvents = availableEvents.filter(p => p.eventConnected || p.eventOccupied);

        if (availableEvents.length > 0 && takenEvents.length === availableEvents.length) {
            headerChecked = true;
        } else {
            headerChecked = false;
        }
    }

    // Function to do proper updates when an event item is clicked
    function selectOne(event) {
        if (multiplePlans) {
            updateSingleEventAllowed(event);
        } else {
            updateEventPartipants(event);
            updateHeaderChecked();
        }
    }

    // Update "all particpants" and "group" events based on change in an event
    function updateEventPartipants(event) {
        const groupEventsConnected = availableEvents.filter(
            p => p.participants.length > 0 && p.eventConnected
        );
        const allParticipantsEventsConnected = availableEvents.filter(
            p => p.participants.length === 0 && p.eventConnected
        );
        const eventSelectedAllParticipants =
            event.participants === undefined || event.participants.length === 0;
        const eventSelectedGroup =
            event.participants !== undefined && event.participants.length > 0;
        const updatedAvailableEvents = [];

        // If an event for "all participants" is clicked, all "group" events must be disabled
        if (eventSelectedAllParticipants) {
            availableEvents.forEach(availableEvent => {
                updatedAvailableEvents.push({
                    eventId: availableEvent.eventId,
                    eventOccupied:
                        availableEvent.participants.length > 0
                            ? event.eventConnected || allParticipantsEventsConnected.length > 0
                            : availableEvent.eventOccupied,
                    eventConnected: availableEvent.eventConnected,
                    eventDate: availableEvent.eventDate,
                    eventTitle: availableEvent.eventTitle,
                    participants: availableEvent.participants
                });
            });
            availableEvents = updatedAvailableEvents;
        }

        // If an event for an "group" event is clicked, "all participants" events must be disabled
        if (eventSelectedGroup) {
            availableEvents.forEach(availableEvent => {
                updatedAvailableEvents.push({
                    eventId: availableEvent.eventId,
                    eventOccupied:
                        availableEvent.participants.length === 0
                            ? event.eventConnected || groupEventsConnected.length > 0
                            : availableEvent.eventOccupied,
                    eventConnected: availableEvent.eventConnected,
                    eventDate: availableEvent.eventDate,
                    eventTitle: availableEvent.eventTitle,
                    participants: availableEvent.participants
                });
            });
            availableEvents = updatedAvailableEvents;
        }
    }

    // Function to enfore only one of the available events can be selected
    // Others will be automatically selected
    function updateSingleEventAllowed(event) {
        const updatedAvailableEvents = [];

        let eventDateEpoch;

        if (event.eventConnected) {
            eventDateEpoch = event.eventFrom.valueOf();
            availableEventsOriginally = [...availableEvents];
        } else {
            availableEvents = [...availableEventsOriginally];
        }

        const selectedPlanIdsCount = planIds.size;

        let autoSelectCounter = 0;

        availableEvents.forEach(availableEvent => {
            const availableEventDateEpoch = availableEvent.eventFrom.valueOf();

            let autoSelectEvent = false;

            if (
                event.eventConnected &&
                !availableEvent.eventOccupied &&
                !availableEvent.eventConnected &&
                availableEventDateEpoch > eventDateEpoch &&
                autoSelectCounter < selectedPlanIdsCount - 1
            ) {
                autoSelectCounter = autoSelectCounter + 1;
                autoSelectEvent = true;
            }

            updatedAvailableEvents.push({
                eventId: availableEvent.eventId,
                eventOccupied: event.eventConnected
                    ? event.eventId !== availableEvent.eventId
                    : availableEvent.eventOccupied,
                eventConnected:
                    event.eventId === availableEvent.eventId
                        ? event.eventConnected
                        : autoSelectEvent,
                eventDate: availableEvent.eventDate,
                eventTitle: availableEvent.eventTitle,
                participants: availableEvent.participants,
                eventFrom: availableEvent.eventFrom,
                eventTo: availableEvent.eventTo
            });
        });
        availableEvents = updatedAvailableEvents;
    }

    // Function to do proper updates when select all checkbox is clicked
    // If mix of "all participants" and "group events" prefer "all participants" events
    function selectAll() {
        const updatedAvailableEvents = [];
        const allParticipantsFreeEvents = availableEvents.filter(
            p => p.participants.length === 0 && !p.eventOccupied
        );
        const groupParticipantsFreeEvents = availableEvents.filter(
            p => p.participants.length > 0 && !p.eventOccupied
        );

        if (allParticipantsFreeEvents.length > 0) {
            allParticipantsFreeEvents[0].eventConnected = headerChecked;
            updateEventPartipants(allParticipantsFreeEvents[0]);
        } else if (groupParticipantsFreeEvents.length > 0) {
            groupParticipantsFreeEvents[0].eventConnected = headerChecked;
            updateEventPartipants(groupParticipantsFreeEvents[0]);
        }

        availableEvents.forEach(availableEvent => {
            const currentEventId = availableEvent.eventId;
            const eventOriginal = allEvents.filter(p => p.eventId === currentEventId);

            updatedAvailableEvents.push({
                eventId: availableEvent.eventId,
                eventOccupied:
                    !headerChecked && eventOriginal[0]
                        ? eventOriginal[0].eventOccupied
                        : availableEvent.eventOccupied,
                eventConnected: availableEvent.eventOccupied ? false : headerChecked,
                eventDate: availableEvent.eventDate,
                eventTitle: availableEvent.eventTitle,
                participants: availableEvent.participants
            });
        });

        availableEvents = updatedAvailableEvents;
    }
</script>
